import React, { useEffect, useState } from 'react';
import Page, { usePage } from '../../components/Page';
import { Button, Grid, TextField } from '@mui/material';
import { SideBar, useSideBar } from '../../components/SideBar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { EnumIcon, EnumIconMap, EnumIconValue } from '../../data/IconMap';
import { api } from '../../api';
import { faBars, faMagnifyingGlass, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { useStyles } from '../../hooks/useStyles';
import { DataGrid, GridColDef, GridColumnHeaderParams, GridRenderCellParams } from '@mui/x-data-grid';
import { MenuConfig } from '../../api/menu-config';
import { useGlobal } from '../../units/global/GlobalProvider';
import { EditMenuConfigDialog, useEditMenuConfigDialog } from './EditMenuConfigDialog';
import { grey } from '@mui/material/colors';

export const useMenuManagePage = () => {
    const page = usePage();
    const classes = useStyles();
    const { menuConfigs, setMenuConfigs, reloadMenuConfigs, reloadUserGroups } = useGlobal();
    const sideBar = useSideBar();
    const editMenuConfigDialog = useEditMenuConfigDialog({ ...page });
    const [inputSearchConfig, setInputSearchConfig] = useState('');
    const columns: GridColDef[] = [
        { field: 'menuConfigId', headerName: 'menuConfigId', flex: 3, minWidth: 90, cellClassName: 'cell', hide: true },
        { field: 'id', headerName: '選單ID', flex: 3, minWidth: 90, cellClassName: 'cell', hide: false },
        { field: 'parentId', headerName: '父層選單ID', flex: 3, minWidth: 90, cellClassName: 'cell', hide: false },
        {
            field: 'icon',
            headerName: '圖示',
            flex: 2,
            minWidth: 60,
            renderCell: (params: GridRenderCellParams) => {
                const row = params.row as MenuConfig;
                return row.iconValue ? <FontAwesomeIcon icon={EnumIconMap[row.iconValue as EnumIconValue]} fixedWidth /> : <></>;
            },
        },
        { field: 'descr', headerName: '選單描述', flex: 3, minWidth: 90, cellClassName: 'cell', hide: false },
        { field: 'navigateTo', headerName: '轉跳路徑', flex: 5, minWidth: 150, cellClassName: 'cell', hide: false },
        { field: 'iconValue', headerName: '圖示', flex: 3, minWidth: 90, cellClassName: 'cell', hide: true },
        { field: 'enable', headerName: '啟用狀態', width: 90, cellClassName: 'cell', hide: false },
        {
            field: 'action',
            renderHeader: (params: GridColumnHeaderParams) => <>操作</>,
            width: 180,
            cellClassName: 'cell',
            renderCell: (params: GridRenderCellParams) => {
                const row = params.row as MenuConfig;
                return (
                    <Grid container justifyContent={'space-around'}>
                        <Button
                            variant='contained'
                            className={classes.buttonWhite}
                            onClick={() => {
                                editMenuConfigDialog.setMenuConfigId(row.menuConfigId || '');
                                editMenuConfigDialog.setInputId(row.id || '');
                                editMenuConfigDialog.setInputParentMenu(menuConfigs.find((v) => v.id == row.parentId) || null);
                                editMenuConfigDialog.setInputDescr(row.descr || '');
                                editMenuConfigDialog.setInputNavigateTo(row.navigateTo || '');
                                editMenuConfigDialog.setInputIconValue(row.iconValue as EnumIconValue | null);
                                editMenuConfigDialog.setIsEnable(row.enable == 'Y');
                                editMenuConfigDialog.setOriginUserGroups(row.userGroups || []);
                                editMenuConfigDialog.onOpen();
                            }}
                        >
                            編輯
                        </Button>
                        <Button
                            variant='contained'
                            className={classes.buttonRed}
                            onClick={() =>
                                page.alert.show({
                                    type: 'warning',
                                    title: '警告',
                                    message: '確定要刪除？',
                                    onConfirm: async () => {
                                        page.setIsLoading(true);
                                        const ret = await api.menuConfig
                                            .remove({ menuConfigId: row.menuConfigId! })
                                            .finally(() => page.setIsLoading(false));
                                        if (ret.success) {
                                            reloadMenuConfigs();
                                            page.simpleAlert.show({ autoHideDuration: 2000, severity: 'success', message: ret.message });
                                        } else {
                                            page.simpleAlert.show({ autoHideDuration: 6000, severity: 'error', message: JSON.stringify(ret) });
                                        }
                                    },
                                })
                            }
                        >
                            刪除
                        </Button>
                    </Grid>
                );
            },
        },
    ];
    useEffect(() => {
        reloadMenuConfigs();
    }, []);
    return { page, classes, sideBar, editMenuConfigDialog, inputSearchConfig, setInputSearchConfig, menuConfigs, columns };
};

export const MenuManagePage = (): React.ReactElement => {
    const { page, classes, sideBar, editMenuConfigDialog, inputSearchConfig, setInputSearchConfig, menuConfigs, columns } =
        useMenuManagePage();
    return (
        <Page {...page} title='選單管理'>
            <Grid container flexDirection={'row'} height={'100%'}>
                <Grid container item flex={1} flexDirection={'column'} padding={4} sx={{ backgroundColor: grey[100] }}>
                    <Grid container item flexDirection={'row'}>
                        <Grid container item flexDirection={'row'} justifyContent={'flex-end'} xs={12}>
                            <TextField
                                value={inputSearchConfig}
                                onChange={(e) => setInputSearchConfig(e.target.value)}
                                variant='outlined'
                                label={
                                    <>
                                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                                        {`搜尋(${['選單ID', '父層選單ID', '選單描述', '轉跳路徑'].join(', ')})`}
                                    </>
                                }
                                style={{ flex: 1, width: '100%', margin: 4, marginTop: 12, backgroundColor: 'white' }}
                            />
                            <Button
                                className={classes.buttonCyan}
                                style={{ margin: 4, marginTop: 12 }}
                                onClick={() => editMenuConfigDialog.onOpen()}
                            >
                                <FontAwesomeIcon icon={faPlus} fixedWidth />
                                {'新增選單'}
                            </Button>
                        </Grid>
                    </Grid>
                    <Grid container item flex={1} padding={2} borderRadius={2} marginTop={2} sx={{ backgroundColor: 'white' }}>
                        <DataGrid
                            rows={(() => {
                                const searchs = inputSearchConfig.split(/\s+/).filter((v) => v.length > 0);
                                return menuConfigs
                                    .filter((v) => v.id !== 'root')
                                    .filter((v, i, a) =>
                                        [
                                            searchs.length == 0
                                                ? true
                                                : searchs
                                                      .map(
                                                          (s) =>
                                                              v.id?.toUpperCase().includes(s.toUpperCase()) ||
                                                              v.parentId?.toUpperCase().includes(s.toUpperCase()) ||
                                                              v.descr?.toUpperCase().includes(s.toUpperCase()) ||
                                                              v.navigateTo?.toUpperCase().includes(s.toUpperCase()),
                                                      )
                                                      .every((v) => v),
                                        ].every((v) => v),
                                    );
                            })()}
                            columns={columns}
                            getRowId={(row: MenuConfig) => row.menuConfigId!}
                            pageSize={10}
                            rowsPerPageOptions={[10]}
                            experimentalFeatures={{ newEditingApi: true }}
                            disableColumnMenu={true}
                            disableSelectionOnClick={true}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <EditMenuConfigDialog {...editMenuConfigDialog} />
        </Page>
    );
};
